import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Rating,
  Stack,
  Typography,
} from '@mui/material';
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { useTranslation } from 'react-i18next';
import type { TutorReviewWithUsername } from '../../models/TutorReview';

const GOLD = '#F8C14A';
const TEAL = '#4AA7C6';
const INITIAL_VISIBLE = 3;

interface TutorReviewsSectionProps {
  reviews: TutorReviewWithUsername[];
  averageRating: number;
  reviewCount: number;
  loading?: boolean;
}

function sortReviewsNewestFirst(reviews: TutorReviewWithUsername[]) {
  return [...reviews].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

export default function TutorReviewsSection({
  reviews,
  averageRating,
  reviewCount,
  loading = false,
}: TutorReviewsSectionProps) {
  const { t, i18n } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  const sortedReviews = useMemo(() => sortReviewsNewestFirst(reviews), [reviews]);
  const visibleReviews = showAll
    ? sortedReviews
    : sortedReviews.slice(0, INITIAL_VISIBLE);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  if (!loading && reviewCount === 0 && sortedReviews.length === 0) {
    return null;
  }

  const displayCount = reviewCount || sortedReviews.length;
  const displayAverage =
    reviewCount > 0 ? averageRating : sortedReviews.length > 0
      ? sortedReviews.reduce((sum, r) => sum + r.rating, 0) / sortedReviews.length
      : 0;

  return (
    <Box
      component="section"
      aria-labelledby="tutor-reviews-heading"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        backgroundImage: `
          radial-gradient(ellipse 120% 80% at 100% 0%, rgba(74, 167, 198, 0.08), transparent 55%),
          linear-gradient(165deg, rgba(248, 193, 74, 0.05) 0%, transparent 42%)
        `,
      }}
    >
      <FormatQuoteRoundedIcon
        aria-hidden
        sx={{
          position: 'absolute',
          top: { xs: -8, sm: 4 },
          right: { xs: 8, sm: 16 },
          fontSize: { xs: 88, sm: 112 },
          color: GOLD,
          opacity: 0.1,
          transform: 'rotate(12deg)',
          pointerEvents: 'none',
        }}
      />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1.5, sm: 3 }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2.5, position: 'relative', zIndex: 1 }}
      >
        <Box>
          <Typography
            id="tutor-reviews-heading"
            variant="overline"
            sx={{
              display: 'block',
              letterSpacing: 2,
              color: TEAL,
              fontWeight: 800,
              lineHeight: 1.2,
            }}
          >
            {t('tutorSearch.reviewsKicker', 'Student voices')}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.15, mt: 0.25 }}>
            {t('tutorSearch.reviewsTitle', 'What students say')}
          </Typography>
        </Box>

        {!loading && displayCount > 0 ? (
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexShrink: 0 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 800, lineHeight: 1, color: 'text.primary' }}
            >
              {displayAverage.toFixed(1)}
            </Typography>
            <Stack spacing={0.25}>
              <Rating
                value={displayAverage}
                readOnly
                precision={0.1}
                size="small"
                icon={<StarRoundedIcon fontSize="inherit" />}
                emptyIcon={<StarRoundedIcon fontSize="inherit" />}
                sx={{ color: GOLD, '& .MuiRating-iconEmpty': { color: 'action.disabled' } }}
              />
              <Typography variant="caption" color="text.secondary">
                {t('review.reviewCount', { count: displayCount })}
              </Typography>
            </Stack>
          </Stack>
        ) : null}
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : sortedReviews.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('tutorSearch.reviewsEmpty', 'No written reviews yet.')}
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
          {visibleReviews.map((review, index) => {
            const comment = review.comment?.trim();
            return (
              <Box
                key={review.id}
                sx={{
                  position: 'relative',
                  pl: 2,
                  pr: { xs: 1.5, sm: 2 },
                  py: 1.75,
                  borderRadius: 1.5,
                  borderLeft: `3px solid ${GOLD}`,
                  bgcolor: index % 2 === 0 ? 'rgba(248, 193, 74, 0.06)' : 'rgba(74, 167, 198, 0.04)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: { sm: 'translateX(4px)' },
                    boxShadow: (theme) => theme.shadows[1],
                  },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 0.75 }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {review.username?.trim() ||
                      t('review.anonymousReviewer', 'Anonymous student')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {formatDate(review.createdAt)}
                  </Typography>
                </Stack>

                <Rating
                  value={review.rating}
                  readOnly
                  size="small"
                  icon={<StarRoundedIcon fontSize="inherit" />}
                  emptyIcon={<StarRoundedIcon fontSize="inherit" />}
                  sx={{
                    color: GOLD,
                    mb: comment ? 0.75 : 0,
                    '& .MuiRating-iconEmpty': { color: 'action.disabled' },
                  }}
                />

                {comment ? (
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: 1.65,
                      color: 'text.primary',
                      fontStyle: 'italic',
                      '&::before': { content: '"\\201C"', color: GOLD, mr: 0.5, fontWeight: 700 },
                    }}
                  >
                    {comment}
                  </Typography>
                ) : null}
              </Box>
            );
          })}

          {sortedReviews.length > INITIAL_VISIBLE ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => setShowAll((prev) => !prev)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  color: TEAL,
                  '&:hover': { bgcolor: 'rgba(74, 167, 198, 0.08)' },
                }}
              >
                {showAll
                  ? t('tutorSearch.showLessReviews', 'Show less')
                  : t('tutorSearch.seeAllReviews', 'See all {{count}} reviews', {
                      count: sortedReviews.length,
                    })}
              </Button>
            </Box>
          ) : null}
        </Stack>
      )}
    </Box>
  );
}
